import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '/@/components/icon';
import settings from '/@/core/settings';
import Title from '/@/v3/components/title';
import CornInformationModules from './corn';
import SoyInformationModules from './soy';

/** @param {typeof propType} props */
function InformationModule(props) {
    const { children, layoutSize, icon, label, isPdf, expanded, expand } = props;
    const [availableHeight, setAvailableHeight] = useState('auto' || 0);
    const rootNodeRef = useRef(null);
    const headerNodeRef = useRef(null);

    useEffect(() => {
        const updateHeight = () => {
            if (!layoutSize) return;
            const rootNode = rootNodeRef.current;
            const headerNode = headerNodeRef.current;
            if (!rootNode || !headerNode) return;
            const containerStyles = getComputedStyle(rootNode.parentNode);
            const defaultSpacing = Number.parseFloat(containerStyles.paddingTop);
            const heightRemainder = defaultSpacing * 3 + defaultSpacing / 2;
            const newAvailableHeight = layoutSize.height - headerNode.offsetHeight - heightRemainder;

            setAvailableHeight(newAvailableHeight);
        };

        updateHeight();
    }, [layoutSize]);

    return (
        <Accordion ref={rootNodeRef} elevation={0} expanded={expanded || isPdf}>
            <AccordionSummary
                ref={headerNodeRef}
                expandIcon={
                    !isPdf && <ExpandMore fontSize='large' sx={{ fontSize: (t) => t.spacing(4) }} color='primary' />
                }
                onClick={expand}
            >
                <Stack direction='row' alignItems='center' spacing={2}>
                    <Icon type={icon} size='md' theme={settings.app.theme} />
                    <Title theme={settings.app.theme}>{label}</Title>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {Boolean(availableHeight) &&
                    React.cloneElement(children, {
                        availableHeight: (availableHeight === 'auto' && availableHeight) || `${availableHeight}px`,
                    })}
            </AccordionDetails>
        </Accordion>
    );
}
const propTypes = {
    children: PropTypes.node.isRequired,
    layoutSize: PropTypes.any,
    icon: PropTypes.any,
    label: PropTypes.any.isRequired,
    isPdf: PropTypes.bool.isRequired,
    defaultOpen: PropTypes.bool.isRequired,
};
InformationModule.propTypes = propTypes;

const getInformationModules = ({
    appInstance,
    recommendation,
    cropConfig,
    productRecommendations,
    brand,
    country,
    catalogBrands,
    isPdf,
    ...rest
}) => {
    const modulesByCrop = {
        corn: CornInformationModules,
        soy: SoyInformationModules,
    };

    const query = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const changeDensityTitle =
        !!query?.get('soil_moisture') &&
        !!query?.get('climate_pattern') &&
        !!query?.get('planting_date') &&
        !!query?.get('product_profile');

    return modulesByCrop[cropConfig.crop]
        .filter(
            ({ condition }) =>
                !condition ||
                condition({
                    recommendation,
                    cropConfig,
                    productRecommendations,
                    brand,
                    country,
                })
        )
        .map(({ component: Component }, index) => {
            const { icon, label } = Component;
            const component = (
                <InformationModule
                    key={Component.key}
                    icon={icon}
                    label={label({ changeDensityTitle })}
                    brand={brand}
                    isPdf={isPdf}
                    defaultOpen={index === 0}
                >
                    <Component
                        appInstance={appInstance}
                        brand={brand}
                        country={country}
                        catalogBrands={catalogBrands}
                        cropConfig={cropConfig}
                        recommendation={recommendation}
                        productRecommendations={productRecommendations}
                        {...rest}
                    />
                </InformationModule>
            );
            return { component, icon, label: label() };
        });
};

export default getInformationModules;
